import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import AdminHeader from "../components/AdminHeader";
import NavSidebar from "../components/NavSidebar";
import { getConversationMessages } from "../api";

function parseTime12hToMs(s) {
  if (!s || typeof s !== "string") return null;
  const match = s.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (!match) return null;
  let [, hStr, mStr, sStr, ampm] = match;
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  const sec = sStr ? parseInt(sStr, 10) : 0;
  if (h < 1 || h > 12 || m > 59 || sec > 59) return null;
  const isPM = ampm.toUpperCase() === "PM";
  if (h === 12) h = 0;
  const hours24 = h + (isPM ? 12 : 0);
  return ((hours24 * 60 + m) * 60 + sec) * 1000;
}

function formatDelta(ms) {
  if (ms == null || ms < 0) return null;
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m later`;
  if (m > 0) return `${m}m ${s}s later`;
  return `${s}s later`;
}

function getMsgSide(m) {
  const s = (m.role || m.sender || m.author || m.source || "").toString().toLowerCase();
  if (/(user|visitor|customer|client|guest|human)/.test(s)) return "user";
  if (/(assistant|agent|bot|system|ai)/.test(s)) return "bot";
  if (m.isUser === true) return "user";
  if (m.isUser === false) return "bot";
  return "bot";
}

function getMsgText(m) {
  const t = m.text ?? m.message ?? m.content ?? m.msg ?? "";
  if (typeof t === "string") return t;
  if (t && typeof t === "object") {
    try { return JSON.stringify(t); } catch { return String(t); }
  }
  return String(t ?? "");
}

function getMsgTime(m) {
  return m.time || m.timestamp || m.date || "";
}

function TranscriptDetailPage() {
  const { transcriptId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const chatRef = useRef(null);

  const [showBackTop, setShowBackTop] = useState(false);

  const projectId = location.state?.projectId;
  const convo = location.state?.convo;

  const missingState = !projectId || !convo || convo.conversationID !== transcriptId;

  // Router state carries conversation metadata only. Messages live in
  // Supabase's conversation_messages table — fetch them once on mount.
  const [fetchedMessages, setFetchedMessages] = useState(null);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState("");
  useEffect(() => {
    if (!transcriptId) return;
    let cancelled = false;
    setMessagesLoading(true);
    setMessagesError("");
    getConversationMessages(transcriptId)
      .then((resp) => { if (!cancelled) setFetchedMessages(resp?.data || []); })
      .catch((e) => { if (!cancelled) { setMessagesError(e?.message || "Failed to load messages"); setFetchedMessages([]); } })
      .finally(() => { if (!cancelled) setMessagesLoading(false); });
    return () => { cancelled = true; };
  }, [transcriptId]);

  const messages = useMemo(() => {
    // Prefer the freshly-fetched turns from conversation_messages; fall back
    // to whatever router state might have carried (legacy compat).
    const raw = Array.isArray(fetchedMessages) && fetchedMessages.length > 0
      ? fetchedMessages
      : Array.isArray(convo?.conversation)
        ? convo.conversation
        : Array.isArray(convo?.messages)
          ? convo.messages
          : [];
    return raw.map((m) => {
      const timeRaw = getMsgTime(m) || m.created_at || "";
      const timeMs = parseTime12hToMs(timeRaw);
      return {
        type: getMsgSide(m),
        text: getMsgText(m),
        timeRaw,
        timeMs,
      };
    });
  }, [fetchedMessages, convo]);
  void messagesLoading; void messagesError;

  const handleScroll = () => {
    if (chatRef.current) setShowBackTop(chatRef.current.scrollTop > 100);
  };

  const scrollToTop = () =>
    chatRef.current?.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <div className="bg-[#f7f8fa] dark:bg-gray-950 min-h-screen transition-colors duration-200">
      <AdminHeader
        username={localStorage.getItem("username") || "admin"}
        initials="AD"
        activePage="Projects"
      />

      <div className="flex">
        <NavSidebar activePage={null} />
        <main className="flex-1 min-w-0">
      <div className="px-10 py-3 text-gray-400 dark:text-gray-500 text-sm flex gap-2 items-center">
        <button
          onClick={() => navigate(-1)}
          className="mr-2 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-xl text-[#32B1FF]"
          title="Back"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path
              d="M15 19l-7-7 7-7"
              stroke="#32B1FF"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <Link to="/dashboard" className="hover:underline">
          / Dashboard{" "}
        </Link>
        <span className="text-gray-400 dark:text-gray-500">/ Transcript /</span>
        <span className="text-[#32B1FF] font-semibold">Detail</span>
      </div>

      <div className="px-10 pb-10 flex flex-col items-center">
        <div className="relative w-full max-w-4xl bg-white dark:bg-gray-900 shadow-xl rounded-xl mt-6 min-h-[600px] flex flex-col p-0 border dark:border-gray-800">
          <div className="flex items-center justify-between border-b dark:border-gray-800 px-8 py-4 sticky top-0 bg-white dark:bg-gray-900 z-10 rounded-t-xl">
            <h2 className="text-lg font-bold text-gray-700 dark:text-gray-200">Transcript</h2>
            <button
              className="rounded hover:bg-gray-100 dark:hover:bg-gray-800 p-1"
              onClick={() => navigate(-1)}
              title="Close"
            >
              <span className="text-2xl font-bold text-gray-400 dark:text-gray-500">&times;</span>
            </button>
          </div>

          <div
            ref={chatRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto px-8 py-8 space-y-4"
            style={{ maxHeight: 450, minHeight: 450 }}
          >
            {missingState ? (
              <div className="text-red-600 dark:text-red-400">
                Conversation data wasn't provided. Please go back to the project's Transcripts list and select a conversation again.
              </div>
            ) : messages.length === 0 ? (
              <div className="text-gray-500 dark:text-gray-400">No messages in this conversation.</div>
            ) : (
              messages.map((msg, idx) => {
                const prev = idx > 0 ? messages[idx - 1] : null;
                const delta =
                  prev && msg.timeMs != null && prev.timeMs != null && msg.timeMs >= prev.timeMs
                    ? formatDelta(msg.timeMs - prev.timeMs)
                    : null;

                return (
                  <div key={idx} className="space-y-1">
                    {idx > 0 && delta && (
                      <div className="flex items-center justify-center">
                        <div className="text-[11px] text-gray-400 dark:text-gray-500 px-2 py-0.5 rounded-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                          {delta}
                        </div>
                      </div>
                    )}

                    {msg.type === "user" ? (
                      <div className="flex justify-end">
                        <div className="max-w-lg">
                          <div className="bg-[#328DFF] text-white rounded-lg px-5 py-3 shadow">
                            {msg.text}
                          </div>
                          {msg.timeRaw ? (
                            <div className="text-right text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                              {msg.timeRaw}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-start">
                        <div className="max-w-lg">
                          <div className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-lg px-5 py-3 shadow border border-gray-200 dark:border-gray-700">
                            {msg.text}
                          </div>
                          {msg.timeRaw ? (
                            <div className="text-left text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                              {msg.timeRaw}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {showBackTop && (
            <button
              onClick={scrollToTop}
              className="absolute bottom-6 right-6 bg-[#328DFF] text-white p-2 rounded-full shadow hover:bg-[#2067c6] z-10 transition"
              title="Back to Top"
            >
              <svg width="22" height="22" fill="none" viewBox="0 0 24 24">
                <path
                  d="M12 19V5m0 0l-7 7m7-7l7 7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
        </main>
      </div>
    </div>
  );
}

export default TranscriptDetailPage;
